import React, { ComponentProps } from 'react';

import PropTypes from 'prop-types';

import { Menu, MenuItem } from '@adsk/alloy-react-dropdown';
import theme from '@adsk/alloy-react-theme';

import PermissionIndicator from './PermissionIndicator';
import PermissionOption from './PermissionOption';
import { PERMISSION_MENU_ITEM_SIZES } from '../consts';
import { itemShape } from '../helpers/propTypes';
import { isGroupHeader } from '../helpers/typeGuards';
import { GroupHeader, ItemSize, Option } from '../types';

type PermissionMenuProps = ComponentProps<typeof Menu> & {
  menuItems: (GroupHeader | Option)[];
  selectedItem?: GroupHeader | Option | null;
  pillCount?: number;
  itemSize?: ItemSize;
  getItemProps: ({
    item,
    index,
  }: {
    item: GroupHeader;
    index: number;
  }) => object;
};

const getItemPadding = ({
  itemSize,
  isFirstItem = false,
  isLastItem = false,
}: {
  itemSize: ItemSize;
  isFirstItem?: boolean;
  isLastItem?: boolean;
}) => {
  const isSmallSize = itemSize === PERMISSION_MENU_ITEM_SIZES.SMALL;

  let top = isSmallSize ? 8 : 12;
  const right = 16;
  let bottom = isSmallSize ? 8 : 12;
  const left = 16;

  if (isFirstItem) {
    top = top + 8;
  }

  if (isLastItem) {
    bottom = bottom + 8;
  }

  return `${top}px ${right}px ${bottom}px ${left}px`;
};

const PermissionMenu = ({
  menuItems: items,
  selectedItem,
  pillCount,
  itemSize = PERMISSION_MENU_ITEM_SIZES.NORMAL,
  getItemProps,
  ...props
}: PermissionMenuProps) => {
  return (
    <Menu {...props}>
      {items.map((item, index) => {
        const isFirstItem = index === 0;
        const isLastItem = index === items.length - 1;

        if (isGroupHeader(item)) {
          return (
            <li
              css={[
                {
                  ...theme.typography.bodySmall,
                  padding: getItemPadding({
                    itemSize,
                    isFirstItem,
                    isLastItem,
                  }),
                  display: 'flex',
                  alignItems: 'center',
                  '&:not(:first-child)': {
                    borderTop: `1px solid ${theme.colors.charcoal200}`,
                  },
                },
              ]}
              key={item.value || index}
            >
              {item.pillValue && (
                <PermissionIndicator
                  style={{
                    marginRight: '6px',
                    flex: 'none',
                  }}
                  pillCount={pillCount}
                  pillValue={item.pillValue}
                />
              )}
              {item.label}
            </li>
          );
        }
        return (
          <MenuItem
            key={index}
            style={{
              padding: getItemPadding({
                itemSize,
                isFirstItem,
                isLastItem,
              }),
            }}
            {...getItemProps({ item, index })}
            disabled={item.disabled}
            isSelected={item.value === selectedItem?.value}
            text={
              <PermissionOption
                style={{ whiteSpace: 'normal' }}
                permission={item}
                disabled={item.disabled}
              />
            }
          />
        );
      })}
    </Menu>
  );
};

PermissionMenu.propTypes = {
  /**
   * The pill count
   */
  pillCount: PropTypes.number,
  /**
   * The option item array
   */
  items: PropTypes.arrayOf(itemShape),
  /**
   * The callback to render menu item
   */
  renderItem: PropTypes.func,
  /**
   * The menu item size
   */
  itemSize: PropTypes.oneOf(Object.values(PERMISSION_MENU_ITEM_SIZES)),
  /**
   * Function to get the props applied to each item
   */
  getItemProps: PropTypes.func.isRequired,
};

export default Object.assign(PermissionMenu, {
  ITEM_SIZES: PERMISSION_MENU_ITEM_SIZES,
});
